import type { ReactNode } from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { cn } from '~/lib/utils'

// 제네릭 데이터 목록 테이블 — 각진 콘솔 표(ui/table) 본체만 공용화한다.
// 검색·상태필터·페이지네이션은 호출부가 그대로 감싸고, 컬럼 정의·로딩/빈/행클릭/선택강조만 config로 받는다.

export interface DataListColumn<T> {
    key: string
    header: ReactNode
    render: (item: T) => ReactNode
    className?: string
    headClassName?: string
    /** 셀(td)에 그대로 다는 title 속성(툴팁). 기존 표의 cell title 동작 보존용. */
    title?: (item: T) => string | undefined
}

export interface DataListTableProps<T> {
    items: T[]
    loading: boolean
    error?: string | null
    columns: DataListColumn<T>[]
    onRowClick?: (item: T) => void
    selectedId?: string
    getRowId: (item: T) => string
    empty?: ReactNode
    loadingText?: ReactNode
}

export function DataListTable<T>({
    items,
    loading,
    error,
    columns,
    onRowClick,
    selectedId,
    getRowId,
    empty = '데이터가 없어요.',
    loadingText = '불러오는 중…',
}: DataListTableProps<T>) {
    return (
        <>
            {error ? <p className='text-destructive mb-4'>{error}</p> : null}

            <Table>
                <TableHeader>
                    <TableRow>
                        {columns.map((col) => (
                            <TableHead
                                key={col.key}
                                className={col.headClassName}>
                                {col.header}
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length}
                                className='text-muted-foreground py-8 text-center'>
                                {loadingText}
                            </TableCell>
                        </TableRow>
                    ) : items.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length}
                                className='text-muted-foreground py-8 text-center'>
                                {empty}
                            </TableCell>
                        </TableRow>
                    ) : (
                        items.map((item) => {
                            const id = getRowId(item)
                            return (
                                <TableRow
                                    key={id}
                                    onClick={() => onRowClick?.(item)}
                                    className={cn(
                                        'cursor-pointer',
                                        id === selectedId &&
                                            'bg-accent hover:bg-accent'
                                    )}>
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.key}
                                            className={col.className}
                                            title={col.title?.(item)}>
                                            {col.render(item)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )
                        })
                    )}
                </TableBody>
            </Table>
        </>
    )
}
